import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Checkbox,
  Container,
  Grid,
  Group,
  Loader,
  NumberFormatter,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  Modal,
  Tooltip,
  Paper,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { usePolling } from "../hooks/usePolling";
import {
  getImport,
  getImportMappings,
  optiviseProducts,
  optiviseProtoBatch,
  retryFailedOptivise,
  setImportKind,
  confirmAsinScrape,
  deleteImport,
} from "../api";
import { useLocation, Link, Redirect } from "wouter";
import { useStore } from "../store";
import {
  IconWand,
  IconArrowRight,
  IconExclamationCircle,
  IconEye,
  IconEyeClosed,
  IconCoins,
  IconSparkles,
  IconInfoCircle,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
} from "@tabler/icons-react";
import { confirmRemoveImport } from "./confirmRemoveImport";
import { useMarketplaces } from "../_Shared/useMarketplaces";
import { skusByKind } from "../utils/skus";
import {
  activatedMarketplaceIds,
  marketplaceLabel,
} from "../utils/activatedMarketplaces";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import ImportStatusBadge from "./ImportStatusBadge";
import ImportProtoListEmbedded from "./ImportProtoListEmbedded";
import { notifications } from "@mantine/notifications";

function mappingStatusKey(status) {
  return (status || "").toLowerCase();
}

function MappingStatusBadge({ status }) {
  let color = "gray";
  const statusText = mappingStatusKey(status);
  switch (statusText) {
    case "processing":
      color = "blue";
      break;
    case "mapped":
      color = "cyan";
      break;
    case "partiallyoptivised":
      color = "teal";
      break;
    case "optivised":
      color = "green";
      break;
    case "failed":
      color = "red";
      break;
    case "queued":
    default:
      color = "gray";
  }

  return (
    <Badge
      leftSection={
        statusText === "processing" ? <Loader size={10} color="white" /> : null
      }
      color={color}
      size="sm"
    >
      {status}
    </Badge>
  );
}

function readKindFromType(type) {
  const t = (type || "").toLowerCase();
  if (t === "asinfile") return "asin";
  if (t === "domestic") return "domestic";
  return "foreign";
}

function isMappingBusy(mapping) {
  const status = mappingStatusKey(mapping?.status);
  return status === "processing" || status === "queued";
}

function mappingProductCount(mapping, rowCount) {
  return mapping?.outputCount || rowCount || 0;
}

// Compact "what we parsed" preview so the user can sanity-check the file BEFORE
// spending credits — the single biggest guard against billing a mis-parsed file.
function catalogueNote(importData, marketplaces) {
  const type = (importData?.type || "").toLowerCase();
  if (
    type !== "domestic" ||
    !importData?.inputMarketplaceId ||
    importData.needsSourceMarketplace
  ) {
    return null;
  }
  const name = marketplaceLabel(
    importData.inputMarketplaceId,
    marketplaces,
    null,
  );
  return name
    ? `We read the column codes as a ${name} export.`
    : "We read the column codes as a marketplace export.";
}

function ParsePreview({ importData, children, caption, note }) {
  const columns = importData.columns ?? [];
  const protos = importData.productVariantProtos ?? [];
  if (columns.length === 0 || protos.length === 0) return children ?? null;

  const previewColumns = columns.slice(0, 10);
  const previewRows = protos.slice(0, 5);
  const hiddenColCount = columns.length - previewColumns.length;
  const tableMinWidth = Math.max(720, previewColumns.length * 120);

  return (
    <Stack gap="sm">
      {caption ?? (
        <Text size="sm">
          We read <b>{importData.rowCount}</b> products across{" "}
          <b>{columns.length}</b> columns.
        </Text>
      )}
      <Table.ScrollContainer minWidth={tableMinWidth}>
        <Table
          layout="fixed"
          w="100%"
          withTableBorder
          withColumnBorders
          highlightOnHover
        >
          <Table.Thead>
            <Table.Tr>
              {previewColumns.map((c) => (
                <Table.Th key={c.key}>{c.name ?? c.key}</Table.Th>
              ))}
              {hiddenColCount > 0 && (
                <Table.Th w={90}>+{hiddenColCount} more</Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {previewRows.map((proto, i) => (
              <Table.Tr key={proto.id ?? i}>
                {previewColumns.map((c) => (
                  <Table.Td key={c.key}>
                    <Text size="sm" lineClamp={1}>
                      {proto.fieldValues?.[c.key] ?? ""}
                    </Text>
                  </Table.Td>
                ))}
                {hiddenColCount > 0 && (
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      …
                    </Text>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {note && <Text size="sm">{note}</Text>}
      {children}
    </Stack>
  );
}

function FileReadCorrection({
  currentKind,
  existingMarketplaceId,
  kindDraft,
  setKindDraft,
  opened,
  onToggle,
  marketplaces,
  sourceValue,
  onSourceChange,
  loadingMarketplaces,
  busy,
  onApply,
}) {
  const draft = kindDraft ?? currentKind;
  const marketplaceChanged =
    draft === "domestic" &&
    !!sourceValue &&
    sourceValue !== existingMarketplaceId;
  const canApply =
    !busy &&
    (draft !== currentKind || marketplaceChanged) &&
    (draft !== "domestic" || !!sourceValue);

  const applyLabel =
    draft === "asin"
      ? "Look up on Amazon"
      : draft === "domestic"
        ? "Translate the codes"
        : "Use these column names";

  const currentReading =
    currentKind === "asin"
      ? "We treated this as a list of Amazon ASINs."
      : currentKind === "domestic"
        ? "We treated this as a marketplace export with coded headers."
        : "We treated this as a product table with named columns.";

  return (
    <Stack gap="xs">
      <Button
        variant="subtle"
        size="compact-sm"
        color="gray"
        justify="flex-start"
        px={0}
        rightSection={
          opened ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
        }
        onClick={onToggle}
      >
        Different kind of file?
      </Button>
      {opened && (
        <Stack gap="sm" pt={4}>
          <Radio.Group
            value={draft}
            onChange={setKindDraft}
            label={currentReading + " Change that if it is wrong."}
          >
            <Stack gap="sm" mt="xs">
              <Radio
                value="foreign"
                label="Product table with named columns"
                description="Headers such as Name, SKU, Price, or Image. We keep these names and map them when you optimise."
              />
              <Radio
                value="domestic"
                label="Marketplace export with codes"
                description="Headers such as 1001, product-id, or shop-sku. We translate them using that marketplace."
              />
              <Radio
                value="asin"
                label="Amazon ASIN list"
                description="A short list of Amazon product IDs such as B0ABC12345, not a full product table. You'll confirm before we look them up on Amazon. This uses credits."
              />
            </Stack>
          </Radio.Group>
          {draft === "domestic" && (
            <Select
              label="Which marketplace was this file exported from?"
              placeholder="Select marketplace"
              data={marketplaces ?? []}
              value={sourceValue}
              onChange={onSourceChange}
              disabled={loadingMarketplaces || busy}
            />
          )}
          <Group>
            <Button
              size="xs"
              loading={busy}
              disabled={!canApply}
              onClick={() => onApply(draft)}
            >
              {applyLabel}
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

function MappingResultCard({
  mapping,
  importId,
  rowCount,
  marketplaceName,
  onRetry,
  onOptiviseMapped,
}) {
  const status = mappingStatusKey(mapping.status);
  const busy = isMappingBusy(mapping);
  const count = mappingProductCount(mapping, rowCount);
  const dest = marketplaceName || "the marketplace";
  const hasOutput = (mapping.outputCount ?? 0) > 0;
  const hasErrors = (mapping.errorProductCount ?? 0) > 0;
  const translated = mapping.type === "domestic" && status === "mapped";
  const nothingNew =
    (status === "optivised" || status === "partiallyoptivised") &&
    mapping.optivisedProductCount === 0 &&
    !hasOutput;
  const showReviewHint = hasOutput && !busy && !translated && !nothingNew;

  let title;
  if (busy) {
    title = (
      <>
        Optivising{" "}
        <NumberFormatter value={rowCount || count} thousandSeparator /> products
        for {dest}…
      </>
    );
  } else if (status === "failed") {
    title = `Could not optivise for ${dest}`;
  } else if (translated) {
    title = (
      <>
        Translated{" "}
        <NumberFormatter value={count} thousandSeparator /> product{" "}
        {count === 1 ? "code" : "codes"} for {dest}
      </>
    );
  } else if (nothingNew) {
    title = <>No new products optivised for {dest}</>;
  } else if (status === "mapped") {
    title = (
      <>
        <NumberFormatter value={count} thousandSeparator /> products stored for{" "}
        {dest}
      </>
    );
  } else {
    title = (
      <>
        <NumberFormatter value={count} thousandSeparator /> products optivised
        for {dest}
      </>
    );
  }

  const showBadge = busy || status === "failed" || status === "partiallyoptivised";

  return (
    <Paper withBorder p="lg" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} size="lg">
            {title}
          </Text>
          {showBadge &&
            (busy ? <Loader size="sm" /> : <MappingStatusBadge status={mapping.status} />)}
        </Group>

        {busy && (
          <Text size="sm" c="dimmed">
            You can leave this page. We will email you when the listings are
            ready to review and download.
          </Text>
        )}

        {translated && !busy && (
          <Text size="sm" c="dimmed">
            This reads the marketplace file and turns the column codes into
            product fields. It does not write the listings. Optivise is the
            next step.
          </Text>
        )}

        {nothingNew && !busy && (
          <Text size="sm" c="dimmed">
            These products were already stored for {dest}. Optivise the
            translated file to write the listings.
          </Text>
        )}

        {showReviewHint && (
          <Text size="sm" c="dimmed">
            Brand, guarantees and compliance details require your confirmation
            and are not automatically completed. Add any missing information
            before uploading to {dest}.
          </Text>
        )}

        {hasErrors && !busy && (
          <Text size="sm" c="red">
            <NumberFormatter
              value={mapping.errorProductCount}
              thousandSeparator
            />{" "}
            {mapping.errorProductCount === 1 ? "product" : "products"} could not
            be optivised
            {mapping.abortReason ? `: ${mapping.abortReason}` : ""}.
          </Text>
        )}

        <Group justify="flex-end" gap="sm" mt={4}>
          {hasErrors && (
            <Button
              component={Link}
              to={`/imports/${importId}/mappings/${mapping.id}`}
              rightSection={<IconArrowRight size={16} />}
              variant="default"
            >
              View errors
            </Button>
          )}

          {hasErrors && status !== "processing" && status !== "queued" && (
            <Tooltip label="Retry only the products that failed. Successful products are left as they are.">
              <Button
                onClick={() => onRetry(mapping)}
                rightSection={<IconSparkles size={16} stroke={1.2} />}
                variant="default"
              >
                Retry failed
              </Button>
            </Tooltip>
          )}

          {status === "mapped" && !hasErrors && (
            <Button
              onClick={() => onOptiviseMapped(mapping)}
              rightSection={<IconWand size={16} stroke={1.2} />}
              color="grape"
            >
              {translated ? "Optivise listings" : "Optivise"}
            </Button>
          )}

          {hasOutput && !nothingNew && (
            <Button
              component={Link}
              to={`/imports/${importId}/mappings/${mapping.id}/products`}
              rightSection={<IconArrowRight size={16} />}
              w={{ base: "100%", xs: "auto" }}
            >
              Review & Download
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}

function ImportDetails({ params }) {
  const [importData, setImportData] = useState(null);
  const [, setLocation] = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const profile = useStore((state) => state.profile);
  const isPartner = profile?.role === "MarketplacePartner";
  const [loadError, setLoadError] = useState(false);
  const [importMappings, setImportMappings] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [override, setOverride] = useState(false);
  const [sourceMarketplace, setSourceMarketplace] = useState(null);
  const [kindBusy, setKindBusy] = useState(false);
  const [showKindPicker, setShowKindPicker] = useState(false);
  const [kindDraft, setKindDraft] = useState(null);
  const [showOriginalFile, setShowOriginalFile] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const setImports = useStore((state) => state.setImports);

  const refresh = useCallback(() => {
    if (isPartner) return;
    getImport(params.id)
      .then((imp) => {
        setImportData(imp);
        setLoadError(false);
        getImportMappings(params.id)
          .then(setImportMappings)
          .catch(() => {});
      })
      .catch(() => {
        setImportData(null);
        setLoadError(true);
      });
  }, [params.id, isPartner]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const preflightPending = importData?.preflightStatus === "running";
  const needsSource = !!importData?.needsSourceMarketplace;
  const mappingsBusy = importMappings?.some((m) => isMappingBusy(m));
  const importBusy =
    importData?.status === "pending" || importData?.status === "processing";

  usePolling(refresh, {
    enabled: preflightPending || needsSource || !!mappingsBusy || !!importBusy,
    intervalMs: 4000,
    invokeImmediately: false,
  });

  const { marketplaces, loading, error } = useMarketplaces();

  const activatedIds = activatedMarketplaceIds(tenantProfile, marketplaces);
  const singleActivated = activatedIds.length === 1;
  const singleActivatedId = singleActivated ? activatedIds[0] : null;
  const preferredMarketplaceId = tenantProfile?.partnerMarketplaceId;
  const existingMappings = importMappings?.map(
    (p) => p.destinationMarketplaceId,
  );
  const availableMarketplaces =
    marketplaces?.filter(
      (marketplace) =>
        activatedIds.includes(marketplace.value) &&
        !existingMappings?.includes(marketplace.value),
    ) ?? [];
  const preferredAvailable = availableMarketplaces.some(
    (m) => m.value === preferredMarketplaceId,
  );
  const singleLabel = marketplaceLabel(
    singleActivatedId,
    marketplaces,
    tenantProfile?.partnerName ||
      (singleActivatedId ? "the marketplace" : null),
  );
  const preferredLabel =
    marketplaceLabel(
      preferredMarketplaceId,
      marketplaces,
      tenantProfile?.partnerName,
    ) ||
    (preferredMarketplaceId ? "B&Q Marketplace" : null);
  const destinationLabel = singleLabel || preferredLabel || "the marketplace";
  const { paid: paidSkusLeft } = skusByKind(tenantProfile?.creditTotals);
  const canPickOtherDestination =
    !singleActivated &&
    availableMarketplaces.length > 1 &&
    paidSkusLeft > 0;
  const destinationForRun =
    selectedMarketplace ||
    singleActivatedId ||
    (preferredAvailable ? preferredMarketplaceId : null);

  if (isPartner) {
    return <Redirect to="/partner" />;
  }

  if (loadError) {
    return (
      <Container size="sm" mt="xl">
        <Stack align="center" gap="md">
          <Title order={2}>Import not found</Title>
          <Text c="dimmed" ta="center">
            This file is not available in your account.
          </Text>
          <Button component={Link} to="/imports" variant="light">
            Back to imports
          </Button>
        </Stack>
      </Container>
    );
  }

  if (!importData) {
    return (
      <Container size="xs">
        <Center>
          <Loader />
        </Center>
      </Container>
    );
  }

  const type = (importData.type || "").toLowerCase();
  const preflightStatus = importData.preflightStatus;
  const preflightMessage = importData.preflightReport?.message;
  const isRejected = preflightStatus === "rejected";
  const asinPending = !!importData.asinScrapePending;
  const asinFetching =
    type === "asinfile" &&
    (importData.status === "pending" || importData.status === "processing") &&
    !asinPending;
  const isBlocking =
    preflightStatus &&
    !["proceed", "notRun", "rejected", "running", "needsMarketplaceProfile"].includes(
      preflightStatus,
    );
  const noMarketplacesConfigured =
    !loading && Array.isArray(marketplaces) && marketplaces.length === 0;
  const allActivatedUsed =
    !loading &&
    activatedIds.length > 0 &&
    availableMarketplaces.length === 0 &&
    (importMappings?.length ?? 0) > 0;
  const noDestinationToStart =
    !loading &&
    (importMappings?.length ?? 0) === 0 &&
    availableMarketplaces.length === 0;
  const optiviseBlocked =
    isBlocking ||
    importBusy ||
    loading ||
    needsSource ||
    asinPending ||
    asinFetching;
  const selectDisabled =
    optiviseBlocked ||
    noMarketplacesConfigured ||
    allActivatedUsed ||
    noDestinationToStart ||
    !!error;

  const currentKind = readKindFromType(type);
  const mappings = importMappings ?? [];
  const hasMappings = mappings.length > 0;
  const useSingleSurface = singleActivated && mappings.length <= 1;

  const disabledReason = asinPending
    ? "Confirm Amazon lookup before optimising."
    : asinFetching
      ? "We're looking up these Amazon listings."
      : needsSource
        ? "We're still reading this file."
        : importBusy
          ? "We're still reading this file."
          : isBlocking
            ? preflightMessage ||
              "This file needs a quick check before it can be optimised."
            : loading
              ? "Loading marketplaces…"
              : error
                ? "Could not load marketplaces. Refresh the page and try again."
                : noMarketplacesConfigured
                  ? "No marketplaces are configured yet."
                  : noDestinationToStart
                    ? "No destination marketplace is available."
                    : allActivatedUsed
                      ? "This file has already been optimised for every marketplace you use."
                      : null;

  const sourceValue =
    sourceMarketplace || importData.inputMarketplaceId || null;

  const applyKind = (kind, marketplaceId = null) => {
    setKindBusy(true);
    setImportKind(importData.id, kind, marketplaceId)
      .then((updated) => {
        setImportData(updated);
        setShowKindPicker(false);
        refresh();
      })
      .catch(() => {})
      .finally(() => setKindBusy(false));
  };

  const toggleReadCorrection = () => {
    if (!showKindPicker) setKindDraft(currentKind);
    setShowKindPicker((open) => !open);
  };

  const openOptiviseModal = () => {
    const dest =
      singleActivatedId ||
      tenantProfile?.partnerMarketplaceId ||
      selectedMarketplace;
    if (dest) setSelectedMarketplace(dest);
    setModalOpen(true);
  };

  const handleRetry = (mapping) => {
    retryFailedOptivise(mapping.id).then(() => {
      notifications.show({
        title: "Retry started",
        message:
          "Failed products are being optimised again. We'll notify you by email when it's done.",
        autoClose: 20_000,
      });
      setImportMappings((prev) =>
        (prev ?? []).map((m) =>
          m.id === mapping.id ? { ...m, status: "processing" } : m,
        ),
      );
    });
  };

  const handleRemove = () => {
    confirmRemoveImport(importData.originalName, () => {
      setDeleting(true);
      deleteImport(importData.id)
        .then(() => {
          setImports((current) =>
            Array.isArray(current)
              ? current.filter((item) => item.id !== importData.id)
              : current,
          );
          notifications.show({ message: "Removed from your imports" });
          setLocation("/imports");
        })
        .catch(() => {
          notifications.show({
            color: "red",
            message: "Could not remove this file. Try again.",
          });
        })
        .finally(() => setDeleting(false));
    });
  };

  const handleOptiviseMapped = (mapping) => {
    optiviseProducts(mapping.id).then(() => {
      notifications.show({
        title: "Optivisation started!",
        message:
          "The selected products are being optivised! We'll notify you by email when it's done.",
        autoClose: 20_000,
      });
      setLocation(
        `/imports/${importData.id}/mappings/${mapping.id}/products`,
      );
    });
  };

  const fileStillWorking = importBusy || preflightPending || mappingsBusy;
  const sourceNote = catalogueNote(importData, marketplaces);

  const kindCorrection =
    !needsSource && !asinPending && !asinFetching && !fileStillWorking ? (
      <FileReadCorrection
        currentKind={currentKind}
        existingMarketplaceId={importData.inputMarketplaceId}
        kindDraft={kindDraft}
        setKindDraft={setKindDraft}
        opened={showKindPicker}
        onToggle={toggleReadCorrection}
        marketplaces={marketplaces}
        sourceValue={sourceValue}
        onSourceChange={setSourceMarketplace}
        loadingMarketplaces={loading}
        busy={kindBusy}
        onApply={(kind) =>
          applyKind(kind, kind === "domestic" ? sourceValue : null)
        }
      />
    ) : null;

  const optiviseButtonLabel = useSingleSurface
    ? `Optivise for ${destinationLabel}`
    : "Select marketplace & Optivise";

  const optiviseCta = (
    <Tooltip label={disabledReason} disabled={!disabledReason} withArrow>
      <span>
        <Button
          onClick={openOptiviseModal}
          color="grape"
          disabled={selectDisabled}
          w={{ base: "100%", xs: "auto" }}
        >
          {optiviseButtonLabel}
        </Button>
      </span>
    </Tooltip>
  );

  const mappingName = (mapping) =>
    marketplaceLabel(
      mapping.destinationMarketplaceId,
      marketplaces,
      destinationLabel,
    );

  const statusShown =
    importData.status && importData.status !== "succeeded";

  return (
    <Container size="xl">
      <Grid>
        <Grid.Col span={showDetails ? 2 : 12} p="md">
          <Group
            align="flex-start"
            justify="space-between"
            wrap="nowrap"
            gap="sm"
            mb="md"
          >
            <Stack gap={3} style={{ minWidth: 0 }}>
              <Text fw={600} lineClamp={2}>
                {importData.originalName}
              </Text>
              <Text fz="sm" c="dimmed">
                <Timeago date={importData.created} />
              </Text>
            </Stack>
            <Group gap="sm" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
              {statusShown && <ImportStatusBadge status={importData.status} />}
              <Button
                size="compact-sm"
                variant="subtle"
                color="gray"
                rightSection={
                  showDetails ? (
                    <IconEyeClosed size={16} />
                  ) : (
                    <IconEye size={16} />
                  )
                }
                onClick={() => setShowDetails((prev) => !prev)}
              >
                {showDetails ? "Hide details" : "Show details"}
              </Button>
              <Tooltip label="Remove from imports">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="Remove from imports"
                  loading={deleting}
                  onClick={handleRemove}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          <Stack gap="lg">
            {needsSource && (
              <Text size="sm" c="dimmed">
                Reading this file…
              </Text>
            )}

            {asinPending && (
              <Alert
                color="orange"
                variant="light"
                icon={<IconExclamationCircle size={16} />}
                title="Look up Amazon listings?"
              >
                We will fetch {importData.rowCount} Amazon listings. This uses{" "}
                {importData.rowCount} SKUs.
                <Group mt="sm">
                  <Button
                    color="orange"
                    loading={kindBusy}
                    onClick={() => {
                      setKindBusy(true);
                      confirmAsinScrape(importData.id)
                        .then((updated) => {
                          setImportData(updated);
                          refresh();
                        })
                        .catch(() => {})
                        .finally(() => setKindBusy(false));
                    }}
                  >
                    Look up Amazon listings
                  </Button>
                  <Button
                    variant="default"
                    disabled={kindBusy}
                    onClick={() => applyKind("foreign")}
                  >
                    This is a product table, not ASINs
                  </Button>
                </Group>
              </Alert>
            )}

            {asinFetching && (
              <Alert
                color="blue"
                variant="light"
                icon={<IconInfoCircle size={16} />}
              >
                Looking up Amazon listings. This can take a few minutes.
              </Alert>
            )}

            {importBusy &&
              !needsSource &&
              !asinPending &&
              !asinFetching &&
              (importData.columns?.length ?? 0) === 0 && (
                <Text size="sm" c="dimmed">
                  Reading this file…
                </Text>
              )}

            {(isRejected ||
              isBlocking ||
              (disabledReason &&
                !needsSource &&
                !asinPending &&
                !asinFetching &&
                !importBusy &&
                !hasMappings &&
                !loading)) && (
              <Alert
                color={isRejected ? "yellow" : "blue"}
                variant="light"
                icon={<IconExclamationCircle size={16} />}
                title={
                  isRejected
                    ? "This file may not optimise well"
                    : isBlocking
                      ? "This file needs a quick check"
                      : "Can't start optimisation yet"
                }
              >
                {isRejected
                  ? `${preflightMessage ?? ""} You can still optimise it, but it may waste credits.`
                  : preflightMessage || disabledReason}
              </Alert>
            )}

            {hasMappings ? (
              <>
                <Stack gap="md">
                  {mappings.map((mapping) => (
                    <MappingResultCard
                      key={mapping.id}
                      mapping={mapping}
                      importId={importData.id}
                      rowCount={importData.rowCount}
                      marketplaceName={mappingName(mapping)}
                      onRetry={handleRetry}
                      onOptiviseMapped={handleOptiviseMapped}
                    />
                  ))}
                </Stack>

                {!useSingleSurface && availableMarketplaces.length > 0 && (
                  <Group justify="flex-end">
                    <Tooltip
                      label={disabledReason}
                      disabled={!disabledReason}
                      withArrow
                    >
                      <span>
                        <Button
                          onClick={openOptiviseModal}
                          variant="default"
                          disabled={selectDisabled}
                        >
                          Optivise for another marketplace
                        </Button>
                      </span>
                    </Tooltip>
                  </Group>
                )}

                {!showDetails && (
                  <Stack gap="xs">
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      color="gray"
                      justify="flex-start"
                      px={0}
                      rightSection={
                        showOriginalFile ? (
                          <IconChevronUp size={14} />
                        ) : (
                          <IconChevronDown size={14} />
                        )
                      }
                      onClick={() => setShowOriginalFile((open) => !open)}
                    >
                      Original file
                    </Button>
                    {showOriginalFile && (
                      <ParsePreview
                        importData={importData}
                        note={sourceNote}
                        caption={
                          <Text size="sm" c="dimmed">
                            What we read from the file.
                          </Text>
                        }
                      >
                        {kindCorrection}
                      </ParsePreview>
                    )}
                  </Stack>
                )}
              </>
            ) : (
              !showDetails && (
                <Stack gap="md">
                  <ParsePreview importData={importData} note={sourceNote}>
                    {kindCorrection}
                  </ParsePreview>
                  <Group justify="flex-end">{optiviseCta}</Group>
                </Stack>
              )
            )}

            {showDetails && !hasMappings && (
              <Group justify="flex-end">{optiviseCta}</Group>
            )}
          </Stack>

          <Modal
            opened={modalOpen}
            onClose={() => setModalOpen(false)}
            title={
              useSingleSurface || preferredAvailable
                ? `Optivise for ${destinationLabel}`
                : "Create new Optivisation"
            }
            centered
          >
            <Stack>
              {useSingleSurface || preferredAvailable ? (
                <Text size="sm">
                  Destination is <b>{destinationLabel}</b>. Trial SKUs from{" "}
                  {destinationLabel} only pay for {destinationLabel} listings.
                  You publish the file in your own back office after you review
                  it.
                </Text>
              ) : (
                <Select
                  disabled={availableMarketplaces.length === 0}
                  label="Destination marketplace"
                  data={availableMarketplaces}
                  value={destinationForRun}
                  onChange={setSelectedMarketplace}
                />
              )}

              {canPickOtherDestination && (
                <Select
                  label="Or pick a different marketplace"
                  description="Paid SKUs only. Trial SKUs will not pay for this destination."
                  data={availableMarketplaces}
                  value={destinationForRun}
                  onChange={setSelectedMarketplace}
                />
              )}

              {!singleActivated &&
                preferredAvailable &&
                availableMarketplaces.length > 1 &&
                paidSkusLeft <= 0 && (
                  <Text size="sm" c="dimmed">
                    Buy SKUs if you need a different marketplace. Trial SKUs
                    cannot pay for it.
                  </Text>
                )}

              {isRejected && (
                <Checkbox
                  checked={override}
                  onChange={(e) => setOverride(e.currentTarget.checked)}
                  label="Optimise anyway — this file didn't pass our pre-check and may use SKUs without a good result."
                />
              )}

              <Group justify="space-between" align="center" mt="xs">
                <Text size="sm" c="dimmed">
                  About to optimise{" "}
                  <b>
                    <NumberFormatter
                      value={importData.rowCount}
                      thousandSeparator
                    />
                  </b>{" "}
                  products
                </Text>
                <Badge
                  size="sm"
                  leftSection={<IconCoins size={12} />}
                  color="gray"
                >
                  <NumberFormatter
                    value={importData.rowCount}
                    thousandSeparator
                  />{" "}
                  SKUs
                </Badge>
              </Group>

              <Button
                loading={isProcessing}
                disabled={
                  !destinationForRun ||
                  optiviseBlocked ||
                  (isRejected && !override)
                }
                rightSection={<IconWand size={18} stroke={1} />}
                mt="sm"
                color="grape"
                onClick={() => {
                  setIsProcessing(true);
                  optiviseProtoBatch(importData.id, {
                    destinationMarketplaceId: destinationForRun,
                    override,
                  })
                    .then((data) => {
                      setImportMappings((prev) => [
                        ...(prev ?? []),
                        { ...data, status: "processing" },
                      ]);
                    })
                    .finally(() => {
                      setIsProcessing(false);
                      setModalOpen(false);
                    });
                }}
              >
                Optivise!
              </Button>
            </Stack>
          </Modal>
        </Grid.Col>
        {showDetails && (
          <Grid.Col span={10} p="md">
            <ImportProtoListEmbedded importData={importData} />
          </Grid.Col>
        )}
      </Grid>
    </Container>
  );
}

export default ImportDetails;
